import { FunctionComponent, useState } from "react";
import Icon from "@mdi/react";
import { mdiChevronDown, mdiChevronUp, mdiPackageVariantClosed } from "@mdi/js";
import moment from "moment";
import { InwardModel } from "../../../../inventory/models/inventory_voucher";
import { InwardAttachmentList } from "../../../../../ui/pages/warehouse_team_management/inward/InwardAttachmentList";

interface GRNAttachmentInput {
  url: string;
  filename?: string;
  type?: string;
  invoice_number?: string;
}

interface GRNSummaryCardProps {
  grnNo: string;
  date?: Date | string;
  createdByName?: string;
  items: InwardModel[];
  projectMap: Record<string, { project_id: string; project: any }>;
  priceMap: Record<string, number>;
  attachments?: GRNAttachmentInput[];
}

const GRNSummaryCard: FunctionComponent<GRNSummaryCardProps> = ({
  grnNo,
  date,
  createdByName,
  items,
  projectMap,
  priceMap,
  attachments = [],
}) => {
  const [expanded, setExpanded] = useState(true);

  const uniqueItems = items.length;
  const totalAccepted = items.reduce(
    (s, it) => s + (it.received_unit_no ?? 0),
    0,
  );
  const totalValue = items.reduce((s, it) => {
    const price = priceMap[it.item?.item_code ?? ""] ?? 0;
    return s + (it.received_unit_no ?? 0) * price;
  }, 0);

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-bold text-blue-700">{grnNo}</span>
          <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium bg-green-50 text-green-700 border-green-200">
            GRN
          </span>
        </div>

        <div className="text-right text-sm text-gray-500">
          <div>{date ? moment(date).format("lll") : "--"}</div>
          {createdByName && (
            <div className="text-xs text-gray-400">by {createdByName}</div>
          )}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm bg-gray-50 p-3 rounded-md">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
            Unique Items
          </p>
          <p className="font-medium text-gray-900">{uniqueItems}</p>
        </div>

        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
            Total Accepted
          </p>
          <p className="font-medium text-green-600">{totalAccepted}</p>
        </div>

        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
            Total Value
          </p>
          <p className="font-medium text-gray-900">
            ₹{totalValue.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Collapsible received items */}
      <div className="border rounded-md overflow-hidden">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium"
        >
          <div className="flex items-center gap-2">
            <Icon path={mdiPackageVariantClosed} size={0.7} />
            <span>View Received Items ({items.length})</span>
          </div>
          <Icon path={expanded ? mdiChevronUp : mdiChevronDown} size={0.8} />
        </button>

        {expanded && (
          <div className="p-3 bg-white divide-y divide-gray-100">
            {items.map((item, itemIdx) => {
              const code = item.item?.item_code ?? "";
              const price = priceMap[code] ?? 0;
              const value = (item.received_unit_no ?? 0) * price;
              const projectInfo = projectMap[code];
              const deviceCount = item.uniqueIds?.length ?? 0;
              const hasDevices =
                item.item?.type === "Product" &&
                (item.item?.subcategory === "Finished Goods" ||
                  item.item?.subcategory === "IT and Electronic Hardware");

              return (
                <div
                  key={item.id ?? itemIdx}
                  className="py-2 first:pt-0 last:pb-0"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">
                        {item.item?.name ?? "--"}
                      </p>
                      <p className="text-xs text-gray-500">{code || "--"}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {item.received_unit_no ?? 0}{" "}
                        {item.item?.unit?.symbol ?? "Qty"}
                      </p>
                      <p className="text-xs text-gray-500">
                        ₹{value.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-gray-500">
                    <div>
                      Total PO Qty:{" "}
                      <span className="text-gray-900 font-medium">
                        {item.total_ordered_qty ?? "--"}
                      </span>
                    </div>
                    <div>
                      Previously Received:{" "}
                      <span className="text-gray-900 font-medium">
                        {item.total_received_qty ?? 0}
                      </span>
                    </div>
                    <div>
                      Project:{" "}
                      <span className="text-gray-900 font-medium">
                        {projectInfo?.project?.name ?? "--"}
                      </span>
                    </div>
                    <div>
                      Cost Center:{" "}
                      <span className="text-gray-900 font-medium">
                        {projectInfo?.project?.cost_center_category ?? "--"}
                      </span>
                    </div>
                    {hasDevices && (
                      <div>
                        Device IDs:{" "}
                        <span className="text-gray-900 font-medium">
                          {deviceCount > 0 ? `${deviceCount} added` : "--"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invoice attachments */}
      {attachments && attachments.length > 0 && (
        <div className="pt-1">
          <InwardAttachmentList
            attachments={attachments.map((a, i) => ({
              url: a.url,
              filename:
                a.filename || a.url?.split("/").pop() || `Attachment ${i + 1}`,
              type: a.type || "",
              invoice_number: a.invoice_number || "-",
              voucher_id: grnNo,
              id: a.filename || `${grnNo}-${i}`,
              category: "inward_attachment",
              size: 0,
            }))}
          />
        </div>
      )}
    </div>
  );
};

export default GRNSummaryCard;
